import { arrayMove } from '@dnd-kit/sortable'
import * as React from 'react'

import { cn } from '~/styled'

import * as Sortable from '../../sortable'
import { Table } from '../../table/Table'
import { AsyncDataState, TAsyncDataResult } from '../DataTable.types'
import { useDataTable } from '../DataTableContext'
import { DataTableHead } from '../DataTableHead'
import { DataTableLoading } from '../DataTableLoading'
import type { DataTableTableProps } from '../DataTableTable'
import { DragAndDropTableBody } from './DragAndDropTableBody'

type DragAndDropTableProps = DataTableTableProps & {
  className?: string
  idColumn?: string
  onDragAndDrop?: (onDragAndDropData: {
    oldIndex: number
    newIndex: number
    newData: TAsyncDataResult
  }) => void
}

export const DragAndDropTable = ({
  idColumn = 'id',
  onDragAndDrop,
  sortable,
  striped,
  theme,
  className,
  ...props
}: DragAndDropTableProps) => {
  const { asyncDataState, data, setData } = useDataTable()
  const isPending = asyncDataState === AsyncDataState.PENDING

  const rowIds = React.useMemo(
    () =>
      data.results.map((row) => {
        return row[idColumn] as string | number
      }),
    [data]
  )
  const handleSortChange = React.useCallback(
    ({ oldIndex, newIndex }) => {
      const sortedResults = arrayMove(data.results, oldIndex, newIndex)
      const newData = { results: sortedResults, total: data.total }
      setData({ results: sortedResults, total: data.total })
      onDragAndDrop?.({ oldIndex, newIndex, newData })
    },
    [data, onDragAndDrop]
  )

  return (
    <Sortable.Root onSortChange={handleSortChange} sortableIds={rowIds}>
      <DataTableLoading />
      <Table
        {...props}
        className={cn(
          isPending && [
            'opacity-50',
            'pointer-events-none',
            'transition-opacity',
            'duration-250',
            'linear',
            'delay-150'
          ],
          className
        )}
      >
        <DataTableHead theme={theme} sortable={sortable} />
        <DragAndDropTableBody striped={striped} idColumn={idColumn} />
      </Table>
    </Sortable.Root>
  )
}
